/**
 * Premium shop / black market panel — Gold-priced catalog, buy-only.
 * Modeled directly on `vendor-ui.ts`'s factory-function pattern (create /
 * open / close / dispose, backdrop-click + Escape to close), simplified:
 * no Sell side, no per-row quantity picker (the catalog's `quantity` is a
 * fixed bundle size delivered per purchase, not a player-chosen amount —
 * see `shop.types.ts`'s `ShopCatalogEntry.quantity` doc comment).
 *
 * ─── Tabs and cards (2026-09-18) ────────────────────────────────────────────
 * Janeks: *"opens a card layout shop with items for sale. Have item categories
 * (tabs), in each tab show a grid of item cards, each card shows image,
 * boost/stats, price, reset time (optional), offer end date timer (optional),
 * buy button. View itself always shows amount of Cash and Gold."*
 *
 * This was a flat text LIST, and **every field that brief asks for was already
 * on the wire** — `category`, `stats`, `offerEndsAt` and the per-player rolling
 * `window` all ship from `GET /shop/premium/catalog` and nothing read them. No
 * endpoint was added for this; the panel caught up with its own contract.
 *
 * The art comes from `pictureHtml` (paperdoll.ts) like every other item surface
 * in the game. The older note here said icons were skipped because `ICON_MAP`
 * "lives unexported inside paperdoll.ts" — it has been exported and shared with
 * home-ui and vendor-ui since, and the arena's hand-rolled `<img src>` 404 on
 * 2026-09-18 is what that kind of local workaround costs.
 *
 * ─── What is NOT a card in the grid ────────────────────────────────────────
 * The deed and the hideout-space offer deliver no `ItemDef`, so they cannot be
 * `ShopItem` rows (`itemDefKey` is NOT NULL — `shop.types.ts` argues this for
 * both). They render as a standing offers strip ABOVE the tab strip rather than
 * inside a tab: the deed is the largest purchase in the game and used to be the
 * first thing in the panel, and putting it behind a tab click would have hidden
 * it to gain nothing.
 */
import { gameStateStore } from '../../state/game-state';
import {
  shopStateStore, loadShopCatalog, buyShopItem, buyHideoutSlots, buyHelpingHand, resetShopState, setShopTab, setShopStorefront,
} from '../../state/shop-state';
import {
  activateAtelier, animateWallet, atelierHeadSlot, atelierRoot, atelierWalletHtml, closeAtelier, deactivateAtelier,
  disposeAtelier, reducedMotion, refreshAtelier,
} from './atelier-ui';
import {
  activateBlackMarket, blackMarketEscape, blackMarketRoot, closeBlackMarket, deactivateBlackMarket, disposeBlackMarket,
} from './black-market-ui';
import {
  homeStateStore, loadHome, buyHomeKey, setHomeKeyConfirm,
} from '../../state/home-state';
import type { HomeState } from '../../state/home-state.types';
import type {
  ShopState, ShopCatalogEntry, ShopHideoutSlotOffer, ShopHelpingHandOffer, ShopStorefront, ShopWindowState,
} from '../../state/shop-state.types';
import { closeOtherModals, registerPanel } from './panels';
import { createWindowChrome, cashAmount, goldAmount, windowHeading } from './window-chrome';
import { goldIconHtml } from './hud';
import { pictureHtml } from './paperdoll';
import { itemTooltipHtml, ItemTooltip } from './item-tooltip';
import { remaining, isHomeOwned } from './home-ui';
import { ammoLabel, formatStatMods } from './stat-format';
import { escapeHtml } from '../../util';

/**
 * Art for the two standing offers that are not items.
 *
 * In `assets/ui/` rather than `assets/icons/`, and NOT through `pictureHtml`:
 * `paperdoll.ts` globs the icons directory wholesale as the item-icon map, and
 * neither of these is a thing the player can pick up or carry. Same split and
 * same reason as the Gold coin in `hud.ts`.
 *
 * The 256px detail tier, not the 512 one — these draw at 92px and the full
 * tier is three times the bytes for pixels nothing shows. One glob per file
 * rather than a wildcard over the directory, so the eight Gold-pack renders
 * that live beside them do not land in the bundle.
 */
// The options have to be an OBJECT LITERAL at each call site: Vite parses
// `import.meta.glob` statically, and a shared `const ART_OPTS` fails the build
// with "Expected the second argument to be an object literal".
const DUFFEL_ART = import.meta.glob<string>('../assets/ui/gold_duffelbag.webp', {
  eager: true, query: '?url', import: 'default',
});
const CRATE_ART = import.meta.glob<string>('../assets/ui/hideout_crate.webp', {
  eager: true, query: '?url', import: 'default',
});
const HAND_ART = import.meta.glob<string>('../assets/ui/helping_hand.webp', {
  eager: true, query: '?url', import: 'default',
});

/**
 * The offer's art, hung over the top-right corner of its card.
 *
 * Decorative and `aria-hidden`: the heading beside it already names the offer,
 * so a screen reader that announced the picture too would say it twice. Empty
 * string when the asset is missing rather than a broken `<img>` — the card
 * still reads without it.
 */
function offerArtHtml(modules: Record<string, string>): string {
  const url = Object.values(modules)[0] ?? '';
  return url ? `<img class="bv-shop-offer-art" src="${url}" alt="" aria-hidden="true" />` : '';
}

let unsubscribers: Array<() => void> = [];
let panelEl: HTMLElement | null = null;

/** The Market's window width, and the Atelier's: it needs room for a stage beside the rail. */
// One window for both storefronts (Janeks, 2026-10-19: "Atelier and the
// regular shop need to be in one, same size"): switching tabs never resizes it.
const SHOP_W = 1180;
const MARKET_W = SHOP_W;
const ATELIER_W = SHOP_W;
let onKeyDown: ((e: KeyboardEvent) => void) | null = null;
let tooltip: ItemTooltip | null = null;

/** Deregisters this panel from `ui/panels.ts` — see `registerPanel`. */
let unregisterPanel: (() => void) | null = null;
/**
 * The purchase prompt (deed / hideout slots). Local, not a store: it is only
 * "what the dialog is asking about right now". For the deed the ask phase is
 * ALSO reachable through `homeStateStore.confirmHomeKey`, which the Escape
 * ladder and `closeShopUI` already knew about.
 */
type PromptKind = 'deed' | 'slots' | 'hand';
interface PromptState { kind: PromptKind; phase: 'ask' | 'working' | 'done' | 'error'; message: string }
let prompt: PromptState | null = null;
let promptEl: HTMLElement | null = null;
let promptSig = '';
let rerenderFn: (() => void) | null = null;
/** The title that sits above the plate, outside the window frame. */
let shopTitleEl: HTMLElement | null = null;

/**
 * The once-a-second repaint, for the two countdowns.
 *
 * Runs ONLY while a card on the current tab actually has a clock — see
 * `syncTick`. Nothing on screen counts itself down: every clock is derived from
 * an ISO instant the server sent, so a backgrounded tab (whose intervals get
 * throttled) comes back correct rather than behind. Same rule and same shape as
 * the Workshop's job timers.
 */
const TICK_MS = 1000;
let tickTimer: ReturnType<typeof setInterval> | null = null;

/**
 * Tab order. Known categories in a deliberate order — meds and ammo are what a
 * player opens this panel for — then anything a future seed row invents, in
 * alphabetical order so it is at least stable. `category` is free text by
 * design (a new tab should be a seed row, not a migration), so an unknown value
 * must get a tab, never be dropped.
 */
const CATEGORY_ORDER = ['PACKS', 'MEDS', 'AMMO', 'ARMS', 'ENERGY', 'MATERIALS', 'COMFORTS'];
/** Where a row with `category: null` goes. */
const UNCATEGORIZED = 'OTHER';

export function createShopUI(): HTMLElement {
  // The scrim, the frame, the close cross and the backdrop-click, from the one
  // shell every window shares — see `ui/window-chrome.ts` for why the six
  // panels stopped each building their own.
  const { root, panel, header, body } = createWindowChrome({
    id: 'shop-ui',
    headerId: 'shop-header',
    bodyId: 'shop-body',
    width: MARKET_W,
    label: 'Close shop',
    onClose: () => closeShopUI(),
    bodyClass: 'bv-shop-body',
  });

  // The window title lives OUTSIDE the plate, over the scrim. `render` writes
  // into `shopTitleEl`; the root becomes a column of [title, panel].
  const titleEl = document.createElement('h2');
  titleEl.className = 'bv-shop-title';
  titleEl.id = 'shop-title';
  shopTitleEl = titleEl;
  const stage = document.createElement('div');
  stage.className = 'bv-shop-stage';
  root.appendChild(stage);
  stage.append(titleEl, panel);
  panel.classList.add('bv-shop-plate');

  // The purchase prompt: a scrim above the stage, inside the same root (the
  // banker's confirm overlay does the same), so Escape and layering are local.
  const pe = document.createElement('div');
  pe.id = 'shop-prompt';
  pe.className = 'bv-scrim bv-shop-prompt';
  pe.style.display = 'none';
  pe.addEventListener('pointerdown', (e) => {
    if (e.target === pe && prompt?.phase !== 'working') closePrompt();
  });
  root.appendChild(pe);
  promptEl = pe;
  promptSig = '';

  // On document.body, like vendor-ui's: `#shop-ui` is `position:fixed;
  // z-index:1001`, a stacking context, so a card appended INTO it could never
  // rise above anything stacked higher. See ItemTooltip's docstring.
  tooltip = new ItemTooltip();

  onKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Escape' || root.style.display === 'none') return;
    e.preventDefault();
    // Innermost first: the deed's "are you sure" is a layer over the shop, and
    // Escaping out of the whole panel from inside it loses the player's place.
    // Same ladder `home-ui.ts` carries — it was written there after exactly
    // this bug, and the confirm moved here with the offer.
    if (prompt || homeStateStore.get().confirmHomeKey) closePrompt();
    else if (shopStateStore.get().storefront === 'BLACK_MARKET' && blackMarketEscape()) return;
    else closeShopUI();
  };
  document.addEventListener('keydown', onKeyDown);

  const rerender = () => {
    if (root.style.display === 'none') { stopTick(); return; }
    render(header, body, shopStateStore.get(), homeStateStore.get());
    syncTick(header, body);
    syncPrompt();
  };
  rerenderFn = rerender;
  unsubscribers = [
    shopStateStore.subscribe(rerender),
    gameStateStore.subscribe(rerender),
    // The deed is sold here, and `homeStateStore` is the single owner of
    // whether it has been bought — see `deedSection` for why it lives in this
    // panel at all.
    homeStateStore.subscribe(rerender),
  ];

  // ONE delegated listener for the whole grid rather than a handler per card:
  // the body is rewritten on every store push and every clock tick, so
  // per-element handlers would be re-bound once a second.
  panelEl = panel;
  body.addEventListener('click', (e) => onBodyClick(e));
  // The storefront switch lives in the header; the Atelier's Top up scrolls
  // with its heading in the body, so the body listens for it too.
  header.addEventListener('click', onHeaderClick);
  body.addEventListener('click', onHeaderClick);
  body.addEventListener('pointerover', onPointerOver);
  body.addEventListener('pointermove', onPointerMove);
  body.addEventListener('pointerleave', () => tooltip?.hide());

  // One registry instead of the five hand-copied guard chains index.ts
  // used to carry. See ui/panels.ts.
  unregisterPanel?.();
  unregisterPanel = registerPanel({ id: 'shop', isOpen: isShopUIOpen, close: closeShopUI });
  return root;
}

export function openShopUI(): void {
  closeOtherModals('shop');
  const el = document.getElementById('shop-ui');
  if (el) el.style.display = 'flex';
  void loadShopCatalog();
  // Refreshes `homeKey.owned` and `homeKey.affordable`. The offer is read out
  // of the home store rather than fetched again here, so there is exactly one
  // writer of home ownership on the client.
  void loadHome();
}

/** Lets index.ts's `interact()`/prompt logic avoid stacking behind an open shop panel — same guard shape as `isVendorUIOpen()`/`isQuestUIOpen()`. */
export function isShopUIOpen(): boolean {
  const el = document.getElementById('shop-ui');
  return !!el && el.style.display !== 'none';
}

export function closeShopUI(): void {
  const el = document.getElementById('shop-ui');
  if (el) el.style.display = 'none';
  // Hands the survivor preview back to the paperdoll with nothing fitted.
  closeAtelier();
  closeBlackMarket();
  stopTick();
  tooltip?.hide();
  resetShopState();
  // Just the confirm flag, never `resetHomeState()` — the home store drives the
  // house's model and its interactivity out in the world, and wiping it on a
  // shop close would board the player's own front door back up.
  setHomeKeyConfirm(false);
  prompt = null;
  promptSig = '';
  if (promptEl) promptEl.style.display = 'none';
}

export function disposeShopUI(): void {
  unregisterPanel?.();
  unregisterPanel = null;
  stopTick();
  for (const fn of unsubscribers) fn();
  unsubscribers = [];
  if (onKeyDown) document.removeEventListener('keydown', onKeyDown);
  onKeyDown = null;
  tooltip?.dispose();
  tooltip = null;
  disposeAtelier();
  disposeBlackMarket();
  panelEl = null;
  shopTitleEl = null;
  promptEl = null;
  prompt = null;
  promptSig = '';
  rerenderFn = null;
}

// ─── tabs ───────────────────────────────────────────────────────────────────

/** The tabs this catalog actually has, in `CATEGORY_ORDER` then alphabetical. */
export function shopTabs(catalog: ShopCatalogEntry[]): string[] {
  const present = new Set(catalog.map((e) => e.category ?? UNCATEGORIZED));
  const known = CATEGORY_ORDER.filter((c) => present.has(c));
  const rest = [...present].filter((c) => !CATEGORY_ORDER.includes(c)).sort();
  return [...known, ...rest];
}

/**
 * The tab to draw.
 *
 * Resolved against the tabs that EXIST rather than trusted from the store: a
 * stored tab whose category has left the catalog (a seed change, a lapsed
 * offer) would otherwise strand the player on an empty grid with no way to see
 * that anything else is for sale.
 */
export function resolveShopTab(tabs: string[], stored: string | null): string | null {
  if (stored && tabs.includes(stored)) return stored;
  return tabs[0] ?? null;
}

// ─── rendering ──────────────────────────────────────────────────────────────

function render(
  header: HTMLElement,
  body: HTMLElement,
  state: ShopState,
  home: HomeState,
): void {
  const character = gameStateStore.get().character;
  const active = character?.state === 'ACTIVE' ? character : null;

  if (state.storefront === 'TAILOR') {
    deactivateBlackMarket();
    renderAtelier(header, body, active?.premiumBalance ?? 0);
    return;
  }
  deactivateAtelier();
  if (state.storefront === 'BLACK_MARKET') {
    renderBlackMarket(header, body);
    return;
  }
  deactivateBlackMarket();
  // The tabs name the storefront; a second heading over the plate would make
  // the market taller than the Atelier.
  if (shopTitleEl) { shopTitleEl.textContent = 'Premium shop'; shopTitleEl.hidden = true; }
  panelEl?.style.setProperty('--win-w', `${MARKET_W}px`);
  panelEl?.classList.remove('bv-shop-atelier');
  body.innerHTML = '';

  // BOTH currencies, per the brief's "always shows amount of Cash and Gold".
  // The Gold coin is the real art (`build_gold_icons.py`), not the star glyph
  // that used to stand in for it — the two disagreeing was the last place in
  // the game where Gold was two different things.
  // Only the storefront tabs stay frozen (Janeks, 2026-10-19: "make the full
  // shop scrollable"); the wallet and messages scroll with the rest.
  header.innerHTML = storefrontSwitchHtml('MARKET');
  body.insertAdjacentHTML('beforeend', `
    <p class="bv-shop-wallet">
      ${active ? cashAmount(active.softBalance) : cashAmount('—')}
      ${active ? goldAmount(active.premiumBalance) : goldAmount('—')}
    </p>
    ${state.error ? `<p class="bv-win-err">${escapeHtml(state.error)}</p>` : ''}
    ${state.lastReceipt ? `<p class="bv-win-ok">${escapeHtml(state.lastReceipt.message)}</p>` : ''}
  `);

  const currentGold = active?.premiumBalance ?? 0;

  // The standing offers — neither is an item, and both stay visible whichever
  // tab is showing. See the header.
  const offers = offersStrip(home, state, currentGold);
  if (offers) body.appendChild(offers);

  if (!state.catalog) {
    body.appendChild(note(state.busy ? 'Loading…' : 'Shop unavailable.'));
    return;
  }
  if (state.catalog.length === 0) {
    body.appendChild(note('Nothing in stock today.'));
    return;
  }

  const tabs = shopTabs(state.catalog);
  const current = resolveShopTab(tabs, state.tab);
  body.appendChild(tabStrip(tabs, current));

  const grid = document.createElement('div');
  grid.className = 'bv-shop-grid';
  const now = Date.now();
  for (const entry of state.catalog) {
    if ((entry.category ?? UNCATEGORIZED) !== current) continue;
    grid.insertAdjacentHTML('beforeend', cardHtml(entry, state.busy, currentGold, now));
  }
  body.appendChild(grid);
}

// ─── the Atelier face ─────────────────────────────────────────────────────

/**
 * The switch at the very top: real tabs, styled like the Survivor Record's
 * (2026-10-19). A view swap, not a category tab (spec 1), so it sits above the
 * heading rather than in the category strip.
 */
export function storefrontSwitchHtml(current: ShopStorefront): string {
  const plate = (key: ShopStorefront, label: string, extra = '', inner = label) => `<button type="button" role="tab"
    class="bv-shop-front${extra}${current === key ? ' bv-shop-front-on' : ''}" aria-selected="${current === key}"
    data-storefront="${key}">${inner}</button>`;
  // The Black Market was a disabled "Coming soon" plate until it opened
  // (2026-10-20): player resale of Gold items, see black-market-ui.ts.
  // Janeks, 2026-10-20: *"Add visual anchor to the Black market tab in Shop.
  // Animation and glow + make it GOLDen."* A gold-leaf plate with the game's
  // own Gold coin turning in it (styles/black-market.css.ts).
  const bm = `<span class="bv-shop-front-bm-coin" aria-hidden="true">${goldIconHtml('bv-shop-front-bm-img')}</span>`
    + '<span class="bv-shop-front-bm-label">Black Market</span>';
  return `<div class="bv-shop-fronts" role="tablist" aria-label="Storefront">
    ${plate('MARKET', 'Premium shop')}${plate('TAILOR', 'Atelier Hollis')}${plate('BLACK_MARKET', 'Black Market', ' bv-shop-front-bm', bm)}</div>`;
}

/** The Black Market face: its root is built once and moved in, like the Atelier's. */
function renderBlackMarket(header: HTMLElement, body: HTMLElement): void {
  if (shopTitleEl) shopTitleEl.hidden = true;
  panelEl?.style.setProperty('--win-w', `${MARKET_W}px`);
  panelEl?.classList.remove('bv-shop-atelier');
  header.innerHTML = storefrontSwitchHtml('BLACK_MARKET');
  const bm = blackMarketRoot();
  if (body.firstElementChild !== bm || body.childElementCount !== 1) {
    body.innerHTML = '';
    body.appendChild(bm);
  }
  activateBlackMarket();
}

function renderAtelier(header: HTMLElement, body: HTMLElement, gold: number): void {
  // The Atelier carries its own heading inside its (already textured) plate.
  if (shopTitleEl) shopTitleEl.hidden = true;
  panelEl?.style.setProperty('--win-w', `${ATELIER_W}px`);
  panelEl?.classList.add('bv-shop-atelier');
  // Only the storefront tabs stay frozen (Janeks, 2026-10-19); the heading and
  // the wallet scroll with the cards, at the top of the Atelier's own column.
  header.innerHTML = storefrontSwitchHtml('TAILOR');
  // The root is built once and only MOVED here: it holds the live 3D canvas.
  const atelier = atelierRoot();
  if (body.firstElementChild !== atelier || body.childElementCount !== 1) {
    body.innerHTML = '';
    body.appendChild(atelier);
  }
  const head = atelierHeadSlot();
  const headHtml = `
    <div class="bv-atl-head">
      <span class="bv-atl-glow" aria-hidden="true"></span>
      <span class="bv-atl-bell" aria-hidden="true"></span>
      ${windowHeading('Atelier Hollis', 'Couture from what\u2019s left. Dress for the day after.')}
      <span class="bv-atl-unroll" aria-hidden="true"></span>
    </div>
    ${atelierWalletHtml(gold)}`;
  // Written once per open, not per repaint: the wallet animates in place.
  if (head && !head.firstElementChild) head.innerHTML = headHtml;
  animateWallet(gold);
  activateAtelier();
  refreshAtelier();
}

function onHeaderClick(e: Event): void {
  const t = e.target as HTMLElement | null;
  const front = t?.closest<HTMLElement>('[data-storefront]');
  if (front) {
    const next = front.dataset.storefront as ShopStorefront;
    if (next === shopStateStore.get().storefront) return;
    // The folding fitting-screen slides across between the two faces (#2).
    if (panelEl && !reducedMotion()) {
      panelEl.classList.remove('bv-shop-swapping');
      void panelEl.offsetWidth;
      panelEl.classList.add('bv-shop-swapping');
    }
    setShopStorefront(next);
    return;
  }
  if (t?.closest('[data-atl-topup]')) {
    void import('./currency-shop-ui').then((m) => m.openCurrencyShopUI());
  }
}

function tabStrip(tabs: string[], current: string | null): HTMLElement {
  const strip = document.createElement('div');
  strip.className = 'bv-shop-tabs';
  strip.setAttribute('role', 'tablist');
  strip.innerHTML = tabs.map((t) => {
    const on = t === current;
    return `<button type="button" role="tab" aria-selected="${on}"`
      + ` class="bv-shop-tab${on ? ' bv-shop-tab-on' : ''}" data-shop-tab="${escapeHtml(t)}">`
      + `${escapeHtml(tabLabel(t))}</button>`;
  }).join('');
  return strip;
}

/** ALL-CAPS wire values read as shouting in a tab. Title-case them for display only. */
export function tabLabel(category: string): string {
  return category.charAt(0) + category.slice(1).toLowerCase();
}

// ─── one card ───────────────────────────────────────────────────────────────

/**
 * PURE, so a test can assert what a given entry renders without mounting the
 * panel — the window state and the two countdowns are the parts worth pinning
 * and the parts with arithmetic in them.
 */
export function cardHtml(
  entry: ShopCatalogEntry,
  busy: boolean,
  currentGold: number,
  now: number,
): string {
  const win = entry.window;
  const lapsed = offerLapsed(entry, now);
  const affordable = currentGold >= entry.goldPrice;
  // The BUTTON follows `remainingPurchases`, never `remaining`: a row whose
  // bundle does not divide its cap leaves units that can never be bought, and
  // gating on those would leave a live button that always rejects.
  const capped = !!win && win.remainingPurchases <= 0;
  const disabled = busy || !affordable || capped || lapsed;

  const art = pictureHtml(entry.iconPath, entry.name, 'bv-shop-card-art', '96px');
  const qty = entry.quantity > 1 ? `<span class="bv-shop-card-qty">×${entry.quantity}</span>` : '';

  const lines: string[] = [];
  const boost = boostLine(entry);
  if (boost) lines.push(`<div class="bv-shop-card-boost">${escapeHtml(boost)}</div>`);
  if (win) lines.push(windowLine(win, now));
  if (entry.offerEndsAt) lines.push(offerLine(entry.offerEndsAt, now));

  const reason = disabled ? disabledReason({
    busy, affordable, capped, lapsed, win, currentGold, entry,
  }) : '';

  return `<div class="well bv-shop-card" data-tip="${escapeHtml(entry.key)}">
    <div class="bv-shop-card-top">${art || '<span class="bv-shop-card-noart" aria-hidden="true">?</span>'}${qty}</div>
    <div class="bv-shop-card-name">${escapeHtml(entry.name)}</div>
    ${lines.join('')}
    <div class="bv-shop-card-foot">
      <span class="bv-shop-card-price">${goldAmount(entry.goldPrice)}</span>
      <button type="button" class="bv-act bv-act-go bv-shop-buy" data-buy="${escapeHtml(entry.key)}"
        ${disabled ? 'disabled' : ''}${reason ? ` title="${escapeHtml(reason)}"` : ''}>Buy</button>
    </div>
  </div>`;
}

/**
 * The card's one stat line — "boost/stats" in the brief.
 *
 * A summary, not the tooltip: the full `ItemStatsSummary` is what hovering the
 * card shows (`itemTooltipHtml`, the same renderer the bag and the stalls use).
 * A card carrying every stat row would be a tooltip that is always open.
 */
export function boostLine(entry: ShopCatalogEntry): string {
  const s = entry.stats;
  const bits: string[] = [];
  if (s.armorPoints != null) bits.push(`${s.armorPoints} AR`);
  if (s.fixedDamage != null) bits.push(`${s.fixedDamage} dmg`);
  else if (s.baseDamage != null) bits.push(`+${s.baseDamage} power`);
  if (s.ammoType) bits.push(ammoLabel(s.ammoType).toLowerCase());
  for (const m of formatStatMods(s.statMods)) bits.push(`${m.value} ${m.label.toLowerCase()}`);
  return bits.join(' · ');
}

/**
 * "0 / 420 left · back in 4h 12m".
 *
 * UNITS, not purchases — the player bought 420 rounds, not "one crate", and the
 * cap Janeks gave is in rounds. `resetsAt` is null while there is allowance
 * left, so there is no cooldown to print and none is printed.
 */
export function windowLine(win: ShopWindowState, now: number): string {
  const left = `${win.remaining} / ${win.limit} left`;
  if (!win.resetsAt) return `<div class="bv-shop-card-win">${escapeHtml(left)}</div>`;
  const ms = Date.parse(win.resetsAt) - now;
  const back = ms > 0 ? ` · back in ${remaining(ms)}` : ' · back now';
  const cls = win.remainingPurchases <= 0 ? ' bv-shop-card-win-spent' : '';
  return `<div class="bv-shop-card-win${cls}" data-clock="win">${escapeHtml(left + back)}</div>`;
}

/** "Offer ends in 2h 5m" — only on rows that carry an expiry. */
export function offerLine(offerEndsAt: string, now: number): string {
  const ms = Date.parse(offerEndsAt) - now;
  const text = ms > 0 ? `Offer ends in ${remaining(ms)}` : 'Offer ended';
  return `<div class="bv-shop-card-offer" data-clock="offer">${escapeHtml(text)}</div>`;
}

export function offerLapsed(entry: ShopCatalogEntry, now: number): boolean {
  return !!entry.offerEndsAt && Date.parse(entry.offerEndsAt) <= now;
}

/**
 * Why the Buy button is dead. A disabled button with no explanation is the
 * shape this repo has shipped before and had to come back for.
 */
function disabledReason(ctx: {
  busy: boolean;
  affordable: boolean;
  capped: boolean;
  lapsed: boolean;
  win: ShopWindowState | null;
  currentGold: number;
  entry: ShopCatalogEntry;
}): string {
  if (ctx.lapsed) return 'This offer has ended.';
  if (ctx.capped && ctx.win) {
    return ctx.win.resetsAt
      ? `Sold out for now — the ${ctx.win.windowHours}h allowance resets in ${remaining(Date.parse(ctx.win.resetsAt) - Date.now())}.`
      : 'Sold out for now.';
  }
  if (!ctx.affordable) return `${ctx.entry.goldPrice - ctx.currentGold} Gold short.`;
  if (ctx.busy) return 'Working…';
  return '';
}

// ─── the standing offers (neither is an item) ───────────────────────────────

function offersStrip(home: HomeState, state: ShopState, currentGold: number): HTMLElement | null {
  // The deed and Helping Hand are mutually exclusive by construction: the deed
  // needs NO home, Helping Hand needs one. Whichever applies takes the first slot.
  const deed = deedSection(home, currentGold);
  const hand = helpingHandSection(state.helpingHand, state.busy);
  const space = hideoutSlotSection(state.hideoutSlots, state.busy, currentGold);
  // The top-up is UNCONDITIONAL — unlike the other two it is never sold out,
  // never already owned and never unaffordable, so it has no null case.
  const topUp = topUpSection();
  const wrap = document.createElement('div');
  wrap.className = 'bv-shop-offers';
  if (deed) wrap.appendChild(deed);
  if (hand) wrap.appendChild(hand);
  wrap.appendChild(topUp);
  if (space) wrap.appendChild(space);
  return wrap;
}

/**
 * Buy more Gold — Janeks: *"Add a topup card into Black market shop."*
 *
 * ─── Why it belongs in the offers strip ─────────────────────────────────────
 * Everything else in this panel is PRICED in Gold, so the one thing a player
 * with no Gold cannot do here is anything at all. The strip already holds the
 * two things that are not catalog items (the deed and the hideout slot), and
 * for the same reason: `ShopItem.itemDefKey` is NOT NULL and `ShopService.buy`
 * delivers an item unconditionally, so a top-up has nothing to point that
 * column at. It is a door to another panel, not a purchase.
 *
 * It opens the currency shop through the SAME `openCurrencyShopUI` the HUD's
 * Gold chip and the action bar use, which is what makes `closeOtherModals`
 * swap the two windows cleanly rather than stacking them — see `ui/panels.ts`.
 * Imported lazily inside the handler to avoid a module cycle: the currency shop
 * does not import this file today, and a static import here would make the
 * pair mutually dependent for one call.
 */
function topUpSection(): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'well bv-shop-topup bv-shop-offer';
  wrap.insertAdjacentHTML('afterbegin', offerArtHtml(DUFFEL_ART));

  const head = document.createElement('div');
  head.className = 'bv-shop-deed-head';
  head.textContent = 'Short on Gold?';
  wrap.appendChild(head);

  const blurb = document.createElement('p');
  blurb.className = 'bv-shop-deed-blurb';
  blurb.textContent = 'Everything here is priced in Gold. Top up.';
  wrap.appendChild(blurb);

  const buy = actionButton('Buy Gold', false);
  buy.classList.add('bv-shop-topup-btn');
  buy.onclick = () => {
    // Lazy, to keep this file and the currency shop from importing each other.
    void import('./currency-shop-ui').then((m) => m.openCurrencyShopUI());
  };
  wrap.appendChild(buy);
  return wrap;
}

/**
 * The Gold "home key" — checklist item 207's instant deed to the house.
 *
 * ─── Why it is HERE and not in the Home panel ───────────────────────────────
 * It used to be the House tab's own button, which was its ONLY surface. On
 * 2026-09-16 the Home panel was closed to players who do not own the house
 * (Janeks: an unowned house "stays on map as an abandoned house"), which would
 * have left a shipped 5 000-Gold purchase reachable by nobody — the exact
 * "unreachable but type-correct" shape this repo has been bitten by before.
 * The black market is where Gold is spent, so the offer moved with the lock.
 *
 * Deliberately not a `ShopItem` catalog row: `ShopItem.itemDefKey` is NOT NULL
 * and `ShopService.buy` delivers an item unconditionally, so an entitlement has
 * nothing to point that column at. `shop.types.ts` makes the same argument for
 * the hideout-slot offer.
 *
 * Returns null once the deed is held (by either path) or before the offer has
 * loaded — a card that says "you already own this" is clutter in a shop.
 */
function deedSection(home: HomeState, _currentGold: number): HTMLElement | null {
  const offer = home.homeKey;
  // `isHomeOwned()` is the Home panel's own predicate, so a home granted by ANY
  // route hides the offer even when the offer payload is stale or never loaded.
  if (!offer || offer.owned || isHomeOwned()) return null;

  const wrap = document.createElement('div');
  wrap.className = 'well bv-shop-deed';

  const head = document.createElement('div');
  head.className = 'bv-shop-deed-head';
  head.textContent = 'Deed to the house';
  wrap.appendChild(head);

  const blurb = document.createElement('p');
  blurb.className = 'bv-shop-deed-blurb';
  blurb.textContent = 'Skip the quest progression, immediate access to hideout';
  wrap.appendChild(blurb);

  // Never disabled for being short: the prompt says how short and offers
  // Buy Gold, which is more useful than a dead button.
  const buy = actionButton(`${offer.goldPrice} Gold`, home.busy);
  buy.onclick = () => { prompt = null; setHomeKeyConfirm(true); };
  wrap.appendChild(buy);
  return wrap;
}

/**
 * "Helping Hand" - 50% faster crafting for 10 hours. Only drawn once the player
 * has a home (`offer.available`, decided by the server from `homeOwned`).
 *
 * One at a time: while a boost runs the card says so with the time left and the
 * button is dead. The server refuses a second purchase either way (ALREADY_ACTIVE).
 */
export function helpingHandSection(
  offer: ShopHelpingHandOffer | null,
  busy: boolean,
  now: number = Date.now(),
): HTMLElement | null {
  if (!offer || !offer.available) return null;

  const wrap = document.createElement('div');
  wrap.className = 'well bv-shop-space bv-shop-offer bv-shop-hand';
  wrap.insertAdjacentHTML('afterbegin', offerArtHtml(HAND_ART));

  const head = document.createElement('div');
  head.className = 'bv-shop-deed-head';
  head.textContent = 'Helping Hand';
  wrap.appendChild(head);

  const left = helpingHandLeftMs(offer, now);
  const blurb = document.createElement('p');
  blurb.className = 'bv-shop-deed-blurb';
  if (left > 0) {
    blurb.dataset.clock = 'hh';
    blurb.textContent = `Active: ${remaining(left)} left.`;
  } else {
    blurb.textContent = 'Crafting 50% faster for 10 hours.';
  }
  wrap.appendChild(blurb);

  const buy = actionButton(left > 0 ? 'Active' : `${offer.goldPrice} Gold`, busy || left > 0);
  buy.onclick = () => { prompt = { kind: 'hand', phase: 'ask', message: '' }; rerenderFn?.(); };
  wrap.appendChild(buy);
  return wrap;
}

/** Milliseconds of boost left at `now`; 0 when none runs (or the instant is unparseable). */
function helpingHandLeftMs(offer: ShopHelpingHandOffer | null, now: number): number {
  const until = offer?.activeUntil ? Date.parse(offer.activeUntil) : NaN;
  return Number.isNaN(until) ? 0 : Math.max(0, until - now);
}

/**
 * "More space in hideout" (checklist 211). Backend shipped 2026-09-16 with no
 * surface at all — `GET`/`POST /shop/premium/hideout-slots` and the column, the
 * formula and the `hideoutSlotBonusPerPurchase` knob behind them were reachable
 * by nothing.
 *
 * Unlike the deed this never goes away: space can be bought again and again, so
 * the card states what has been bought already and what the cap stands at now.
 */
function hideoutSlotSection(
  offer: ShopHideoutSlotOffer | null,
  busy: boolean,
  _currentGold: number,
): HTMLElement | null {
  if (!offer) return null;

  const wrap = document.createElement('div');
  wrap.className = 'well bv-shop-space bv-shop-offer';
  wrap.insertAdjacentHTML('afterbegin', offerArtHtml(CRATE_ART));

  const head = document.createElement('div');
  head.className = 'bv-shop-deed-head';
  head.textContent = 'More room in the hideout';
  wrap.appendChild(head);

  const blurb = document.createElement('p');
  blurb.className = 'bv-shop-deed-blurb';
  blurb.textContent = offer.purchasedBonus > 0
    ? `+${offer.slotsPerPurchase} slots. You paid for ${offer.purchasedBonus} already; stash holds ${offer.hideoutSlotCap}.`
    : `+${offer.slotsPerPurchase} slots. Stash holds ${offer.hideoutSlotCap}.`;
  wrap.appendChild(blurb);

  const buy = actionButton(`${offer.goldPrice} Gold`, busy);
  buy.onclick = () => { prompt = { kind: 'slots', phase: 'ask', message: '' }; rerenderFn?.(); };
  wrap.appendChild(buy);
  return wrap;
}

// ─── the purchase prompt ────────────────────────────────────────────────────

function closePrompt(): void {
  prompt = null;
  setHomeKeyConfirm(false);
  rerenderFn?.();
}

/** What the prompt should show now, or null. */
function currentPrompt(home: HomeState): PromptState | null {
  if (prompt) return prompt;
  if (home.confirmHomeKey && home.homeKey && !home.homeKey.owned) return { kind: 'deed', phase: 'ask', message: '' };
  return null;
}

function confirmPurchase(kind: PromptKind): void {
  prompt = { kind, phase: 'working', message: '' };
  rerenderFn?.();
  const run = kind === 'deed' ? buyHomeKey() : kind === 'hand' ? buyHelpingHand() : buyHideoutSlots();
  // A started craft reads the bench's boost, so refresh it after Helping Hand.
  const refreshBench = kind === 'hand';
  void run.then(() => {
    if (refreshBench && !shopStateStore.get().error) void loadHome();
    if (!prompt || prompt.kind !== kind) return;
    if (kind === 'deed') {
      const h = homeStateStore.get();
      prompt = h.error
        ? { kind, phase: 'error', message: h.error }
        : { kind, phase: 'done', message: h.notice ?? 'Done.' };
    } else {
      const sh = shopStateStore.get();
      prompt = sh.error
        ? { kind, phase: 'error', message: sh.error }
        : { kind, phase: 'done', message: sh.lastReceipt?.message ?? 'Done.' };
    }
    setHomeKeyConfirm(false);
    rerenderFn?.();
  });
}

/** Draws (or hides) the prompt. Rebuilt only when its content changes, so focus survives clock ticks. */
function syncPrompt(): void {
  if (!promptEl) return;
  const home = homeStateStore.get();
  const p = currentPrompt(home);
  if (!p) { promptEl.style.display = 'none'; promptSig = ''; return; }

  const character = gameStateStore.get().character;
  const gold = character?.state === 'ACTIVE' ? character.premiumBalance : 0;
  const deed = p.kind === 'deed';
  const slotOffer = shopStateStore.get().hideoutSlots;
  const handOffer = shopStateStore.get().helpingHand;
  const price = deed ? home.homeKey?.goldPrice : p.kind === 'hand' ? handOffer?.goldPrice : slotOffer?.goldPrice;
  // After a purchase the offer may be gone (the deed is owned now); the result
  // phases do not need it.
  const shown = price ?? 0;
  if (price === undefined && p.phase === 'ask') { promptEl.style.display = 'none'; promptSig = ''; return; }
  const short = Math.max(0, shown - gold);
  const sig = `${p.kind}|${p.phase}|${p.message}|${shown}|${short > 0}`;
  promptEl.style.display = 'flex';
  if (sig === promptSig) return;
  promptSig = sig;

  const title = deed ? 'Deed to the house' : p.kind === 'hand' ? 'Helping Hand' : 'More room in the hideout';
  const gets = deed
    ? 'Skip the quest progression, immediate access to hideout'
    : p.kind === 'hand'
      ? 'Crafting 50% faster for 10 hours. One at a time: it cannot be stacked or extended.'
      : `+${slotOffer?.slotsPerPurchase ?? 0} hideout slots. Stash holds ${slotOffer?.hideoutSlotCap ?? 0} today.`;

  const panel = document.createElement('div');
  panel.className = 'panel grained bv-win bv-shop-prompt-panel';
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-modal', 'true');
  panel.setAttribute('aria-labelledby', 'shop-prompt-title');
  panel.tabIndex = -1;
  panel.style.setProperty('--win-w', '420px');

  const h = document.createElement('h3');
  h.id = 'shop-prompt-title';
  h.className = 'bv-win-title';
  h.textContent = title;
  panel.appendChild(h);

  const row = document.createElement('div');
  row.className = 'bv-act-row bv-shop-prompt-acts';
  const cancel = (label: string): HTMLButtonElement => {
    const b = actionButton(label, false, true);
    b.onclick = () => closePrompt();
    return b;
  };

  if (p.phase === 'ask') {
    const what = document.createElement('p');
    what.className = 'bv-shop-prompt-line';
    what.textContent = gets;
    const cost = document.createElement('p');
    cost.className = 'bv-shop-prompt-line';
    cost.innerHTML = `Price: ${goldAmount(shown)}`;
    panel.append(what, cost);
    if (short > 0) {
      const warn = document.createElement('p');
      warn.className = 'bv-win-err';
      warn.textContent = `${short} Gold short.`;
      panel.appendChild(warn);
      const buyGold = actionButton('Buy Gold', false);
      buyGold.onclick = () => {
        prompt = null;
        setHomeKeyConfirm(false);
        rerenderFn?.();
        void import('./currency-shop-ui').then((m) => m.openCurrencyShopUI());
      };
      row.append(buyGold, cancel('Cancel'));
    } else {
      if (deed) {
        const note = document.createElement('p');
        note.className = 'bv-shop-deed-ask';
        note.textContent = 'Gold does not come back.';
        panel.appendChild(note);
      }
      const yes = actionButton(`Pay ${shown} Gold`, false);
      yes.onclick = () => confirmPurchase(p.kind);
      row.append(yes, cancel('Cancel'));
    }
  } else if (p.phase === 'working') {
    const w = document.createElement('p');
    w.className = 'bv-shop-prompt-line';
    w.textContent = 'Working…';
    panel.appendChild(w);
    row.appendChild(actionButton('Cancel', true, true));
  } else {
    const m = document.createElement('p');
    m.className = p.phase === 'error' ? 'bv-win-err' : 'bv-win-ok';
    m.setAttribute('role', 'status');
    m.textContent = p.message;
    panel.appendChild(m);
    row.appendChild(cancel('Close'));
  }
  panel.appendChild(row);
  promptEl.replaceChildren(panel);
  (panel.querySelector<HTMLButtonElement>('button:not(:disabled)') ?? panel).focus();
}

/** The shared button, at the shop's two weights — commit and cancel. */
function actionButton(label: string, disabled: boolean, muted = false): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = muted ? 'bv-act bv-act-ghost' : 'bv-act bv-act-go';
  // "5000 Gold" / "Pay 5000 Gold" wear the coin; the unit stays for screen readers.
  const gold = /^(Pay )?(\d[\d,]*) Gold$/.exec(label);
  if (gold) b.innerHTML = `${gold[1] ?? ''}${goldAmount(gold[2]!)}`;
  else b.textContent = label;
  b.disabled = disabled;
  return b;
}

// ─── delegated events ───────────────────────────────────────────────────────

function onBodyClick(e: Event): void {
  const target = e.target as HTMLElement | null;
  const tab = target?.closest<HTMLElement>('[data-shop-tab]');
  if (tab?.dataset.shopTab) { setShopTab(tab.dataset.shopTab); return; }
  const buy = target?.closest<HTMLElement>('[data-buy]');
  if (buy?.dataset.buy) void buyShopItem(buy.dataset.buy);
}

function onPointerOver(e: PointerEvent): void {
  const card = (e.target as HTMLElement | null)?.closest<HTMLElement>('[data-tip]');
  if (!card?.dataset.tip) { tooltip?.hide(); return; }
  const entry = shopStateStore.get().catalog?.find((c) => c.key === card.dataset.tip);
  if (!entry) { tooltip?.hide(); return; }
  tooltip?.show(itemTooltipHtml({
    name: entry.name,
    kind: entry.kind,
    stats: entry.stats,
    priceLine: entry.quantity > 1
      ? `${entry.goldPrice} Gold for ${entry.quantity}`
      : `${entry.goldPrice} Gold`,
  }), e.clientX, e.clientY);
}

function onPointerMove(e: PointerEvent): void {
  if (!tooltip?.isVisible) return;
  if (!(e.target as HTMLElement | null)?.closest('[data-tip]')) { tooltip.hide(); return; }
  tooltip.move(e.clientX, e.clientY);
}

// ─── the clock ──────────────────────────────────────────────────────────────

/**
 * Start or stop the repaint, following whether anything ON SCREEN has a clock.
 *
 * Idempotent in both directions: the store pushes on every write and
 * restarting the interval on each one would make the countdown stutter. Same
 * shape as `home-ui.ts`'s `syncTick`, which was written for the craft timers.
 */
function syncTick(header: HTMLElement, body: HTMLElement): void {
  const state = shopStateStore.get();
  const wanted = isShopUIOpen() && (
    !!state.catalog?.some((e) => e.offerEndsAt || e.window?.resetsAt)
    || helpingHandLeftMs(state.helpingHand, Date.now()) > 0
  );
  if (wanted && !tickTimer) {
    tickTimer = setInterval(() => {
      if (!isShopUIOpen()) { stopTick(); return; }
      // A full render ONLY when the patch says it cannot cope — see below.
      if (!patchClocks(body, shopStateStore.get(), Date.now())) {
        render(header, body, shopStateStore.get(), homeStateStore.get());
      }
    }, TICK_MS);
  } else if (!wanted) {
    stopTick();
  }
}

/**
 * Rewrite the countdown text in place. Returns false when a full render is
 * needed instead.
 *
 * ─── Why this is not just `render()` once a second ──────────────────────────
 * It WAS, and it made every card in the grid a brand new element every second.
 * Nothing broke for a player holding still — the click handler is delegated on
 * the body, which survives — but it broke three things that matter:
 *
 *  - **Hover.** A card is replaced under the pointer mid-transition, so the
 *    lift, the art's scale and the Buy button's sheen all restart from zero
 *    once a second. The hover polish Janeks asked for could not settle.
 *  - **The tooltip**, which re-fires `pointerover` on the replacement node.
 *  - **Every pointer-driven tool**, including Playwright: `probe.mjs shop-ammo`
 *    had been timing out on `page.click` and `shop-hover` on `page.hover`,
 *    while the vendor panel — which patches its own clock span in place — was
 *    fine. That is what finally identified this; it was recorded as
 *    "pre-existing and environmental" before, which was wrong.
 *
 * `vendor-ui.ts` has patched its restock span in place since it was written,
 * for the neighbouring reason (a re-render there wipes a half-typed quantity).
 * This is the same move.
 *
 * A full render is still demanded when an offer LAPSES, because that changes
 * more than text: the Buy button has to die and its reason has to change. That
 * is once per offer, not once per second.
 */
function patchClocks(body: HTMLElement, state: ShopState, now: number): boolean {
  const catalog = state.catalog;
  if (!catalog) return false;
  // Helping Hand's own clock; when it lapses the button must come back, which
  // is more than text, so that takes the full pass.
  const hh = body.querySelector<HTMLElement>('[data-clock="hh"]');
  if (hh) {
    const left = helpingHandLeftMs(state.helpingHand, now);
    if (left <= 0) return false;
    hh.textContent = `Active: ${remaining(left)} left.`;
  }
  for (const entry of catalog) {
    const card = body.querySelector<HTMLElement>(`[data-tip="${cssEscape(entry.key)}"]`);
    if (!card) continue;
    // A card that lapsed while the panel sat open needs the full pass.
    const buy = card.querySelector<HTMLButtonElement>('[data-buy]');
    if (offerLapsed(entry, now) && buy && !buy.disabled) return false;
    const win = card.querySelector<HTMLElement>('[data-clock="win"]');
    if (win && entry.window) win.textContent = textOf(windowLine(entry.window, now));
    const offer = card.querySelector<HTMLElement>('[data-clock="offer"]');
    if (offer && entry.offerEndsAt) offer.textContent = textOf(offerLine(entry.offerEndsAt, now));
  }
  return true;
}

/** The text of a one-element HTML string — the two line builders return markup. */
function textOf(html: string): string {
  const el = document.createElement('div');
  el.innerHTML = html;
  return el.textContent ?? '';
}

/**
 * A shop key inside an attribute selector. The keys are seeded slugs
 * (`ammo_rifle_crate`), so this has never had anything to escape — but a
 * selector built from data is a selector that can be broken by data, and the
 * seed is an authoring surface.
 */
function cssEscape(value: string): string {
  return value.replace(/["\\]/g, '\\$&');
}

function stopTick(): void {
  if (tickTimer) clearInterval(tickTimer);
  tickTimer = null;
}

function note(text: string): HTMLElement {
  const p = document.createElement('p');
  p.className = 'bv-win-empty';
  p.textContent = text;
  return p;
}

